import * as React from "react";
import {
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import {} from "@material-ui/pickers";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import GeneralModal from "../modals/GeneralModal";
import FlashMessage from "../shared/FlashMessage";
import InputMask from "react-input-mask";
import globals from "../globals/globals";
import Modal from "../modals/Modal";
import ClearIcon from "@mui/icons-material/Clear";
import { useFlash } from "../FlashContext";

interface Props {
  selectedOrganization: any;
  showAddOrganizationModal: boolean;
  setShowAddOrganizationModal: any;
  setIsEditing: any;
  placeOfServiceList: any[];
  setRefreshOrganizations: any;
  setSelectedOrganization: any;
  updateOrganizationInList: any;
}

const AddOrganizationModal: React.FC<Props> = ({
  selectedOrganization,
  showAddOrganizationModal,
  setShowAddOrganizationModal,
  setIsEditing,
  placeOfServiceList,
  setRefreshOrganizations,
  setSelectedOrganization,
  updateOrganizationInList,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const normalizedZip = selectedOrganization?.zip?.replace(/-/g, "") || "";

  const [name, setName] = React.useState<string>(
    selectedOrganization?.name || "",
  );
  const [address, setAddress] = React.useState<string>(
    selectedOrganization?.address ?? "",
  );
  const [addressLine2, setAddressLine2] = React.useState<string>(
    selectedOrganization?.address_line_2 ?? "",
  );
  const [city, setCity] = React.useState<string>(
    selectedOrganization?.city ?? "",
  );
  const [state, setState] = React.useState<string>(
    selectedOrganization?.state ?? "",
  );
  const [zip, setZip] = React.useState<string>(
    normalizedZip ? normalizedZip.slice(0, 5) : "",
  );
  const [zipExtension, setZipExtension] = React.useState<string>(
    normalizedZip && normalizedZip.length === 9 ? normalizedZip.slice(5) : "",
  );
  const [county, setCounty] = React.useState<string>(
    selectedOrganization?.county ?? "",
  );
  const [phoneNumber, setPhoneNumber] = React.useState<string>(
    selectedOrganization?.phone_number ?? "",
  );
  const [notes, setNotes] = React.useState<string>(
    selectedOrganization?.notes ?? "",
  );
  const [facilityNpi, setFacilityNpi] = React.useState<string>(
    selectedOrganization?.facility_npi ?? "",
  );
  const [federalTaxId, setFederalTaxId] = React.useState<string>(
    selectedOrganization?.federal_tax_id ?? "",
  );
  const [placeOfServiceCode, setPlaceOfServiceCode] = React.useState<string>(
    selectedOrganization?.place_of_service_code ?? "",
  );
  const [idQualifier, setIdQualifier] = React.useState<string>(
    selectedOrganization?.id_qualifier ?? "",
  );
  const [providerNumber, setProviderNumber] = React.useState<string>(
    selectedOrganization?.provider_number ?? "",
  );
  const [openDeactivateProviderModal, setOpenDeactivateProviderModal] =
    React.useState(false);

  const [loading, setLoading] = React.useState<boolean>(false);

  const modalContent = (
    <div className="modal-content">
      <p className="align-center">
        You are attempting to deactivate this organization. Would you like to
        continue?
      </p>
    </div>
  );

  const idQualifiersOptions = [
    { value: "0B", label: "0B - State License Number" },
    { value: "G2", label: "G2 - Provider Commercial Number" },
    { value: "ZZ", label: "ZZ - Provider Taxonomy" },
  ];

  const handleModalClose = () => {
    event.preventDefault();
    setShowAddOrganizationModal(false);
    setIsEditing(false);
  };

  const handleCloseDeactivateModal = () => {
    setOpenDeactivateProviderModal(false);
  };

  const validForm = () => {
    if (!name || name?.length < 1) {
      showToast({
        title: "Organization Name Required",
        text: "Organization Name is required",
        type: "error",
      });
      return false;
    }
    if (!address && address?.length < 1) {
      showToast({
        title: "Address Required",
        text: "Address is required",
        type: "error",
      });
      return false;
    }
    if (!city || city?.length < 1) {
      showToast({
        title: "City Required",
        text: "City is required",
        type: "error",
      });
      return false;
    }
    if (!state || state?.length < 1) {
      showToast({
        title: "State Required",
        text: "State is required",
        type: "error",
      });
      return false;
    }
    if (!zip || zip?.length < 5) {
      showToast({
        title: "Zip Code Required",
        text: "ZIP Code is required and must be a valid 5-digit number",
        type: "error",
      });
      return false;
    }
    if (!zipExtension || zipExtension?.length < 4) {
      showToast({
        title: "Zip Extension Required",
        text: "ZIP Extension is required and must be a valid 4-digit number",
        type: "error",
      });
      return false;
    }
    if (!phoneNumber || phoneNumber?.length < 1) {
      showToast({
        title: "Phone Number Required",
        text: "Phone Number is required",
        type: "error",
      });
      return false;
    }
    if (!/^\d{9}$/.test(federalTaxId)) {
      showToast({
        title: "Federal Tax ID Required",
        text:
          "Federal Tax ID # is required and must be a valid 9-digit number",
        type: "error",
      });
      return false;
    }
    if (!facilityNpi || !/^\d{10}$/.test(facilityNpi)) {
      showToast({
        title: "Facility NPI Required",
        text: "Facility NPI is required and must be a valid 10-digit number",
        type: "error",
      });
      return false;
    }

    if (providerNumber.trim() && !idQualifier) {
      showToast({
        title: "ID Qualifier Required",
        text: "ID Qualifier is required when Provider Number is provided",
        type: "error",
      });
      return false;
    }

    if (idQualifier && !providerNumber.trim()) {
      showToast({
        title: "Provider Number Required",
        text: "Provider Number is required when ID Qualifier is provided",
        type: "error",
      });
      return false;
    }

    return true;
  };

  const addOrganization = () => {
    if (validForm()) {
      let zipCode = "";
      const sanitizedZip = zip.trim();
      const sanitizedZipExtension = zipExtension ? zipExtension.trim() : "";

      if (sanitizedZipExtension.length === 4) {
        zipCode = sanitizedZip + sanitizedZipExtension;
      } else {
        zipCode = zip;
      }

      setLoading(true);
      fetch(`/data_fetching/customers`, {
        method: "POST",
        headers: getHeaders(authenticationSetting?.csrfToken),
        body: JSON.stringify({
          customer: {
            name: name,
            address: address,
            address_line_2: addressLine2,
            city: city,
            state: state,
            zip: zipCode,
            county: county,
            phone_number: phoneNumber,
            notes: notes,
            facility_npi: facilityNpi,
            federal_tax_id: federalTaxId,
            place_of_service_code: placeOfServiceCode,
            id_qualifier: idQualifier || null,
            provider_number: providerNumber.trim() || null,
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success) {
            setLoading(false);
            setShowAddOrganizationModal(false);
            setRefreshOrganizations(true);
            showToast({
              title: "Organization Added",
              text: "Organization has been added to Starfield",
              type: "success",
            });
          } else {
            setLoading(false);
            const errorMessage =
              result.message || "Failed to create organization";
            showToast({
              title: "Creation Failed",
              text: errorMessage,
              type: "error",
            });
          }
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
          showToast({
            title: "Creation Failed",
            text: "An unexpected error occurred while creating the organization.",
            type: "error",
          });
        });
    }
  };

  const updateOrganization = () => {
    if (validForm()) {
      let zipCode = "";
      const sanitizedZip = zip.trim();
      const sanitizedZipExtension = zipExtension ? zipExtension.trim() : "";

      if (sanitizedZipExtension.length === 4) {
        zipCode = sanitizedZip + sanitizedZipExtension;
      } else {
        zipCode = zip;
      }
      setLoading(true);
      fetch(`/data_fetching/customers/${selectedOrganization?.id}`, {
        method: "PUT",
        headers: getHeaders(authenticationSetting?.csrfToken),
        body: JSON.stringify({
          customer: {
            name: name,
            address: address,
            address_line_2: addressLine2,
            city: city,
            state: state,
            zip: zipCode,
            county: county,
            phone_number: phoneNumber,
            notes: notes,
            facility_npi: facilityNpi,
            federal_tax_id: federalTaxId,
            place_of_service_code: placeOfServiceCode,
            id_qualifier: idQualifier || null,
            provider_number: providerNumber.trim() || null,
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success) {
            setLoading(false);
            setShowAddOrganizationModal(false);
            updateOrganizationInList(result.resource);
            setSelectedOrganization(result.resource);
            showToast({
              title: "Organization Updated",
              text: "Organization has been updated",
              type: "success",
            });
          } else {
            setLoading(false);
            const errorMessage =
              result.message || "Failed to update organization";
            showToast({
              title: "Update Failed",
              text: errorMessage,
              type: "error",
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log(error);
          showToast({
            title: "Update Failed",
            text: "An unexpected error occurred while updating the organization.",
            type: "error",
          });
        });
    }
  };

  const handleCustomerArchive = () => {
    console.log("archiving customer");
    fetch(`/data_fetching/customers/${selectedOrganization?.id}`, {
      method: "DELETE",
      headers: getHeaders(authenticationSetting?.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success) {
          handleCloseDeactivateModal();
          showToast({
            title: "Organization Deactivated",
            text: "Organization deactivated successfully",
            type: "success",
          });
          setShowAddOrganizationModal(false);
          setRefreshOrganizations(true);
        } else {
          showToast({
            title: "Deactivation Failed",
            text: "Failed to deactivate organization",
            type: "success",
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const stateOptions = globals.states.map((state) => {
    return (
      <MenuItem key={state} value={state}>
        {state}
      </MenuItem>
    );
  });

  return (
    <GeneralModal
      open={showAddOrganizationModal}
      title={selectedOrganization ? "Edit Organization" : "Add Organization"}
      successCallback={
        selectedOrganization ? updateOrganization : addOrganization
      }
      closeCallback={handleModalClose}
      containerClassName="add-provider-modal"
      width="675px"
      isLoading={false}
      confirmButtonText={selectedOrganization ? "Save" : "Create"}
      cancelButtonText="Back"
    >
      <Grid container className="form-container" spacing={1}>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="name"
                className="field-label"
                sx={{
                  marginTop: "0px !important",
                  paddingTop: "0px !important",
                }}
              >
                Name*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="name"
                size="small"
                value={name}
                className="the-field"
                required
                variant="outlined"
                onChange={(event) => {
                  setName(event.target.value);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="address" className="field-label">
                Address*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="address"
                size="small"
                value={address}
                className="the-field"
                required
                variant="outlined"
                onChange={(event) => {
                  setAddress(event.target.value);
                }}
                slotProps={{
                  htmlInput: { maxLength: 30 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="address_line_2" className="field-label">
                Address Line 2
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="address_line_2"
                size="small"
                value={addressLine2}
                className="the-field"
                variant="outlined"
                onChange={(event) => {
                  setAddressLine2(event.target.value);
                }}
                slotProps={{
                  htmlInput: { maxLength: 30 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="city" className="field-label">
                City*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="city"
                size="small"
                value={city}
                className="the-field"
                required
                variant="outlined"
                onChange={(event) => {
                  setCity(event.target.value);
                }}
                slotProps={{
                  htmlInput: { maxLength: 30 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="state" className="field-label">
                State*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="state"
                value={state}
                required
                size="small"
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setState(event.target.value);
                }}
                select
                slotProps={{
                  input: {
                    endAdornment: state && (
                      <InputAdornment
                        position="end"
                        sx={{ marginRight: "12px" }}
                      >
                        <ClearIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setState("");
                          }}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                {stateOptions}
              </TextField>
            </Grid>
          </Grid>

          <Grid container justifyContent="space-between">
            <Grid container xs={5}>
              <Grid item xs={12}>
                <InputLabel htmlFor="zip" className="field-label">
                  ZIP Code*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="zip"
                  size="small"
                  value={zip}
                  required
                  variant="outlined"
                  className="the-field"
                  onChange={(event) => {
                    const zipCode = event.target.value.replace(/\D/g, "");
                    setZip(zipCode);
                  }}
                  slotProps={{
                    htmlInput: { inputMode: "numeric", maxLength: 5 },
                  }}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs="auto"
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "1.5rem",
                  marginTop: "57px",
                  color: "grey",
                }}
              >
                –
              </span>
            </Grid>
            <Grid container xs={5} sx={{ mr: 1 }}>
              <Grid item xs={12}>
                <InputLabel htmlFor="zipExtension" className="field-label">
                  Extension*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="zipExtension"
                  size="small"
                  value={zipExtension}
                  required
                  variant="outlined"
                  className="the-field"
                  onChange={(event) => {
                    const zipCodeExtension = event.target.value.replace(
                      /\D/g,
                      "",
                    );
                    setZipExtension(zipCodeExtension);
                  }}
                  slotProps={{
                    htmlInput: { inputMode: "numeric", maxLength: 4 },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="career"
                className="field-label"
                sx={{
                  marginTop: "0px !important",
                  paddingTop: "0px !important",
                }}
              >
                County
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="county"
                size="small"
                value={county}
                className="the-field"
                variant="outlined"
                onChange={(event) => {
                  setCounty(event.target.value);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="credentials" className="field-label">
                Phone Number*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <InputMask
                id="mobile-phone-number"
                mask="999-999-9999"
                value={phoneNumber}
                disabled={false}
                maskChar=""
                variant="outlined"
                className="the-field"
                size="small"
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                }}
              >
                {() => (
                  <TextField
                    variant="outlined"
                    size="small"
                    className="the-field"
                  />
                )}
              </InputMask>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="providerNpiNumber" className="field-label">
                Federal Tax ID #*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="federal_tax_id"
                size="small"
                value={federalTaxId}
                required
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setFederalTaxId(event.target.value);
                }}
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 9 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="facilityNpi" className="field-label">
                Facility NPI*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="facilityNpi"
                size="small"
                value={facilityNpi}
                required
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setFacilityNpi(event.target.value);
                }}
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 10 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="idQualifier" className="field-label">
                ID Qualifier
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="idQualifier"
                value={idQualifier}
                size="small"
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setIdQualifier(event.target.value);
                }}
                select
                slotProps={{
                  input: {
                    endAdornment: idQualifier && (
                      <InputAdornment
                        position="end"
                        sx={{ marginRight: "12px" }}
                      >
                        <ClearIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setIdQualifier("");
                          }}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                {idQualifiersOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="idNumber" className="field-label">
                Provider Number
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="providerNumber"
                size="small"
                value={providerNumber}
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setProviderNumber(event.target.value);
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="placeOfServiceCode" className="field-label">
                Place of Service Code
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="placeOfServiceCode"
                value={placeOfServiceCode}
                required
                size="small"
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setPlaceOfServiceCode(event.target.value);
                }}
                select
                slotProps={{
                  input: {
                    endAdornment: placeOfServiceCode && (
                      <InputAdornment
                        position="end"
                        sx={{ marginRight: "12px" }}
                      >
                        <ClearIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlaceOfServiceCode("");
                          }}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                {placeOfServiceList?.map((option) => (
                  <MenuItem
                    key={option.code}
                    value={`${option.code} - ${option.name}`}
                  >
                    {`${option.code} - ${option.name}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        {selectedOrganization?.deleteable && (
          <Grid container className="form-container">
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 30,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenDeactivateProviderModal(true)}
                style={{
                  textTransform: "none",
                  color: "#FF0000",
                  border: "1px solid #FF0000",
                  backgroundColor: "#FFFFFF",
                  fontWeight: "bold",
                  borderRadius: 6,
                  padding: "8px 24px",
                  boxShadow: `0px 1px 5px 0px #0000001F, 0px 2px 3px 0px #00000024, 0px 3px 5px -2px #00000033`,
                }}
              >
                Deactivate Organization
              </Button>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="notes"
                className="field-label"
                sx={{ marginTop: "0px !important" }}
              >
                Notes
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="notes"
                size="small"
                value={notes}
                className="the-field"
                multiline
                rows={4}
                variant="outlined"
                placeholder="Enter Notes"
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
                sx={{
                  width: "97.5% !important",
                  "& .MuiOutlinedInput-root": {
                    padding: "0px !important",
                  },
                  "& .MuiInputBase-inputMultiline": {
                    padding: "10.5px !important",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Modal
        successModalOpen={openDeactivateProviderModal}
        setSuccessModalOpen={handleCloseDeactivateModal}
        successHeader={"Deactivate Organization"}
        successContent={modalContent}
        successCallback={handleCustomerArchive}
        closeCallback={handleCloseDeactivateModal}
        confirmButtonText="Deactivate"
      />
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </GeneralModal>
  );
};

export default AddOrganizationModal;
